import React from 'react';
import { Box, useTheme } from '@mui/material';
import {
  Typography,
  Icon,
  ColumnLayout,
  RowLayout,
  KeyValuePair,
} from '@/components/atoms';
import { CreditScoreBlockProps } from './CreditScoreBlock.props';

/**
 * CreditScoreBlock - Displays credit score information
 *
 * Shows the average credit score prominently and optionally displays
 * individual scores from each credit bureau (Equifax, Experian, TransUnion)
 * with their respective logos.
 */
export const CreditScoreBlock: React.FC<CreditScoreBlockProps> = ({
  averageCreditScore,
  creditScores = [],
  showBureauScores = false,
}) => {
  const theme = useTheme();

  // Determine color based on credit score range
  const getCreditScoreColor = (score: number): string => {
    if (score >= 300 && score < 410) {
      return theme.palette.State.error;
    } else if (score >= 410 && score < 520) {
      return theme.palette.State.warning;
    } else if (score >= 520 && score < 630) {
      return theme.palette.Mortgage.State['Mild Warning'];
    } else if (score >= 630 && score < 740) {
      return theme.palette.Mortgage.State['Light Success'];
    } else if (score >= 740 && score <= 850) {
      return theme.palette.Mortgage.State.Success;
    }
    return theme.palette.M3.sys.light['on-surface']; // Default
  };

  return (
    <ColumnLayout spacing="md" alignItems="flex-start" fullWidth>
      {/* Label */}
      <Typography
        sx={{
          ...theme.typography.M3.body.medium,
          color: theme.palette.M3.sys.light['on-surface-variant'],
        }}
      >
        Credit Score
      </Typography>

      {/* Content Row */}
      <RowLayout
        alignItems="flex-start"
        fullWidth
        sx={{
          gap: theme.unit(6),
        }}
      >
        {/* Average Credit Score */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: 'max-content',
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              ...theme.typography.M3.headline.large,
              fontWeight: 700,
              color: getCreditScoreColor(averageCreditScore),
            }}
          >
            {averageCreditScore}
          </Typography>
        </Box>

        {/* Individual Bureau Scores */}
        {showBureauScores && creditScores.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexGrow: 1,
              overflow: 'auto',
            }}
          >
            <RowLayout
              spacing="xl"
              alignItems="flex-end"
              fullWidth
              justifyContent="space-between"
            >
              {creditScores.map((score) => (
                <KeyValuePair
                  key={score.bureau}
                  fullWidth
                  direction="vertical"
                  spacing="xs"
                  align="center"
                  flex={1}
                  sx={{
                    height: '48px',
                    justifyContent: 'flex-end',
                  }}
                  labelSx={{
                    maxHeight: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 1,
                  }}
                  valueSx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  label={
                    <Icon
                      src={score.icon}
                      alt={score.bureau}
                      sx={{
                        width: '100%',
                        height: 'auto',
                        maxHeight: '100%',
                      }}
                    />
                  }
                  value={
                    <Typography
                      sx={{
                        ...theme.typography.M3.label.large,
                        fontWeight: 600,
                      }}
                    >
                      {score.score}
                    </Typography>
                  }
                />
              ))}
            </RowLayout>
          </Box>
        )}
      </RowLayout>
    </ColumnLayout>
  );
};

export default CreditScoreBlock;
